/**
 * @fileoverview Settings tab: list workspaces the signed-in user owns or is a member of.
 *
 * @purpose Renders `/settings/workspaces` content inside `UserProfile` when the `my-workspaces` tab is active.
 * @usage Imported by `UserProfile.tsx`; data from `GET /api/workspaces` (session cookie).
 * @ownership `@korvo/core` profile components
 * @dependencies Browser `fetch` to same-origin `/api/workspaces`.
 * @restrictions Do not import Prisma or server-only modules; do not invent default API URLs.
 *
 * @docs docs/USER_STORIES_USER_SETTINGS_MENU_ALPHA.md
 */

'use client'

import { type ReactElement, useEffect, useState } from 'react'

type WorkspaceRow = {
  id: string
  name: string
  createdAt?: string
}

export function MyWorkspacesTab(): ReactElement {
  const [workspaces, setWorkspaces] = useState<WorkspaceRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await fetch('/api/workspaces', { credentials: 'include' })
        if (!res.ok) {
          const text = await res.text().catch(() => '')
          throw new Error(text.length > 0 ? text : `HTTP ${String(res.status)}`)
        }
        const data = (await res.json()) as { workspaces?: WorkspaceRow[] }
        if (cancelled) return
        setWorkspaces(Array.isArray(data.workspaces) ? data.workspaces : [])
      } catch (e: unknown) {
        if (cancelled) return
        setError(e instanceof Error ? e.message : String(e))
        setWorkspaces([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  if (workspaces === null && error === null) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold text-foreground-default">My Workspaces</h2>
        <p className="text-sm text-foreground-muted">Loading…</p>
      </div>
    )
  }

  if (error !== null && error !== '') {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold text-foreground-default">My Workspaces</h2>
        <p className="text-sm text-danger-default">{error}</p>
      </div>
    )
  }

  const list = workspaces ?? []

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-foreground-default">My Workspaces</h2>
      <p className="text-sm text-foreground-muted">Workspaces you own or belong to.</p>
      {list.length === 0 ? (
        <p className="text-sm text-foreground-muted">You have no workspaces yet</p>
      ) : (
        <ul data-testid="my-workspaces-list" className="divide-y divide-border-default">
          {list.map((ws) => (
            <li key={ws.id} className="py-3">
              <div className="font-medium text-foreground-default">{ws.name}</div>
              {ws.createdAt !== undefined && ws.createdAt !== '' ? (
                <div className="text-xs text-foreground-muted">
                  Created {new Date(ws.createdAt).toLocaleDateString()}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
